<script>
  // Full-screen, keyboard-driven review of a queue of quest submissions. It is
  // queue-agnostic: the caller owns where the submissions come from (the paged
  // list filters, the moderation feed, …) and where Exit goes.
  //
  //   <ReviewSurface
  //     queue={{ items, totalCount, hasMore, loadMore }}
  //     startId={start_id}
  //     rejectionCategories={rejection_categories}
  //     curationLevels={curation_levels}
  //     exitLabel="Back to list"
  //     onExit={() => router.visit(exit_path)}
  //   />
  //
  // `queue.items` are `submission_props(review: true)`-shaped and may grow while
  // the surface is open; items are never expected to leave it, because ←, U and
  // the sibling Example lookup depend on reviewed items staying put.
  // `onDecision(id, kind, example)` / `onUndo(id)` fire once the server confirms.
  //
  // Each decision is an async Inertia visit that redirects back to the hosting
  // page (lib/inertia_action.js). `reloadOnly` names the props that redirect
  // refreshes — by default only `errors`, so the surface stays put and the global
  // flash stays quiet; the surface shows its own toasts.
  import { untrack } from 'svelte'
  import { router } from '@inertiajs/svelte'
  import dayjs from 'dayjs'
  import localizedFormat from 'dayjs/plugin/localizedFormat'
  import ClickableImage from '../lightbox/clickable_image.svelte'
  import { Button, Icon } from '../ui'
  import VisibilityBadge from '../badges/visibility_badge.svelte'
  import ActionToast from './action_toast.svelte'
  import { inertiaAction } from '../../lib/inertia_action.js'
  import {
    confirmedLevel as confirmedLevelOf,
    decisionCounts,
    decisionFailure,
    dropDecision as withoutDecision,
    latestRating,
    pressRating,
    questHasExample as hasExampleFor,
    recordDecision,
    resetRating,
    settleRating,
    skipDecision,
    startIndex,
    undoable
  } from '../../lib/review_decisions.js'

  dayjs.extend(localizedFormat)

  const DEFAULT_ENDPOINTS = {
    approve: (id) => `/quest-submissions/${id}/approve`,
    reject: (id) => `/quest-submissions/${id}/reject`,
    delete: (id) => `/quest-submissions/${id}/delete_moment`,
    unreview: (id) => `/quest-submissions/${id}/unreview`,
    rate: (id) => `/quest-submissions/${id}/rate`
  }

  let {
    queue = { items: [], totalCount: 0, hasMore: false, loadMore: () => {} },
    startId = null,
    rejectionCategories = [],
    // The server's rating levels: [{ value, label, definition, shortcut }].
    curationLevels = [],
    exitLabel = 'Back to list',
    endpoints = {},
    onExit = () => {},
    onDecision = null,
    onUndo = null,
    reloadOnly = ['errors']
  } = $props()

  const CAPTURE_MODE_LABELS = { photo: 'Photo', dual_photo: 'Dual photo', video: 'Video' }
  const endpointFor = (action, id) => (endpoints[action] || DEFAULT_ENDPOINTS[action])(id)
  const send = (action, id, body) => inertiaAction(router, 'patch', endpointFor(action, id), body, { only: reloadOnly })

  // -- Queue state ------------------------------------------------------------
  // Seeded from the queue as mounted, so a queue that is already full (the feed
  // overlay) opens straight on the clicked item with no loading frame.
  const initialIds = untrack(() => queue.items.map((item) => item.id))
  let phase = $state('main')
  let queueIds = $state(initialIds)
  let index = $state(untrack(() => startIndex(initialIds, startId)))
  let startApplied = initialIds.length > 0

  let decisions = $state({})
  // Ids of this session's server-affecting decisions (approve / reject / delete —
  // never skips), most recent last. U pops the top and un-reviews it.
  let undoStack = $state([])
  let undoBusy = $state(false)
  // Per-submission in-flight decision request, so an undo pressed right after a
  // decision waits for it to land instead of racing it.
  const inFlight = {}
  // Rating shown ahead of the server (optimistic), per submission. Presses on one
  // submission run one after another, behind any in-flight approve of it; the
  // press bookkeeping (lib/review_decisions.js `pressRating`…) decides which press
  // may write the shown level and which landed level a failure falls back to.
  let levelOverrides = $state({})
  const rateQueue = {}
  let ratings = {}
  // Quests the server told us already have an Example — the fetched
  // quest.has_example is stale for items loaded before the Example was
  // granted elsewhere in this session.
  let exampleQuestIds = $state(new Set())
  let rejectOpen = $state(false)
  let deleteOpen = $state(false)
  let rejectCategory = $state('')
  let rejectReason = $state('')
  let selectedRuleId = $state(null)
  let toast = $state(null)

  const itemsById = $derived.by(() =>
    Object.fromEntries(queue.items.map((item) => [item.id, item]))
  )

  // During the main pass newly loaded items append to the queue. The skipped
  // pass reviews a fixed subset, so it never grows.
  $effect(() => {
    const ids = queue.items.map((item) => item.id)
    untrack(() => {
      if (phase !== 'main') return
      const added = ids.filter((id) => !queueIds.includes(id))
      if (added.length > 0) queueIds = [...queueIds, ...added]
    })
  })

  // Jump to the row the moderator clicked, once it shows up in the first page.
  $effect(() => {
    if (startApplied || queueIds.length === 0) return
    startApplied = true
    if (startId) {
      const startIndex = queueIds.indexOf(startId)
      if (startIndex > 0) index = startIndex
    }
  })

  // Keep 2-3 pages of runway ahead of the moderator.
  $effect(() => {
    // Untracked: loadMore reads the caller's loading flag, and tracking it would
    // re-fire this effect (and the same request) every time a failed load settles.
    if (phase === 'main' && queue.hasMore && index >= queueIds.length - 5) untrack(() => queue.loadMore?.())
  })

  // Prefetch the next few items' media so advancing feels instant.
  $effect(() => {
    queueIds.slice(index + 1, index + 4).forEach((id) => {
      const moment = itemsById[id]?.moment
      ;[moment?.main_image_url, moment?.selfie_image_url, moment?.thumbnail_url].forEach((url) => {
        if (url) new Image().src = url
      })
    })
  })

  const currentId = $derived(queueIds[index])
  const current = $derived(currentId ? itemsById[currentId] : undefined)
  const currentDecision = $derived(currentId ? decisions[currentId] : undefined)

  const isPending = $derived(!!current && current.state === 'submitted' && !currentDecision)
  const isApproved = $derived(!!current && (current.state === 'approved' || currentDecision?.kind === 'approved'))
  const currentLevel = $derived(
    current ? (levelOverrides[current.id] ?? currentDecision?.curation_level ?? current.curation_level ?? 'none') : 'none'
  )
  const levelByKey = $derived(Object.fromEntries(curationLevels.map((level) => [level.shortcut, level.value])))
  const definitionsLine = $derived(curationLevels.map((level) => `${level.label}: ${level.definition}`).join(' · '))
  // A quest can only hold one Example, and the server rolls back the whole
  // approve+example on a duplicate — so count Examples granted to sibling
  // submissions earlier this session, not just the fetched value.
  const questHasExample = $derived(hasExampleFor(current?.quest, { exampleQuestIds, decisions, itemsById }))

  const notifyError = (title, text = null) => (toast = { type: 'error', title, text })
  const notifySuccess = (title) => (toast = { type: 'success', title })

  const closePanels = () => {
    rejectOpen = false
    deleteOpen = false
  }

  const advance = () => {
    closePanels()
    index += 1
  }

  const goBack = () => {
    closePanels()
    index = Math.max(0, index - 1)
  }

  const applyState = (next) => {
    decisions = next.decisions
    undoStack = next.undoStack
  }

  const dropDecision = (id) => applyState(withoutDecision({ decisions, undoStack }, id))

  // A review decision or an undo resets the rating state: a rating press that is
  // still in flight must not overwrite what the server just did.
  const forgetRating = (id) => {
    ratings = resetRating(ratings, id)
    if (!(id in levelOverrides)) return
    const { [id]: _dropped, ...rest } = levelOverrides
    levelOverrides = rest
  }

  // Optimistic advance: record the decision and move on immediately; the
  // request settles in the background. Failures surface as a toast, clear the
  // decision so ← can revisit the item, and drop it from the undo stack — only
  // a review that actually landed can be undone (U).
  const runDecision = (submission, action, body, decision) => {
    const id = submission.id
    forgetRating(id)
    applyState(recordDecision({ decisions, undoStack }, id, decision))
    advance()

    const settled = (async () => {
      const result = await send(action, id, body)
      if (result.ok) {
        onDecision?.(id, decision.kind, decision.example === true)
        return true
      }

      const failure = decisionFailure({ decisions, undoStack }, id, result)
      applyState(failure)
      if (failure.exampleExists && submission.quest?.id) exampleQuestIds = new Set(exampleQuestIds).add(submission.quest.id)
      notifyError(failure.toast.title, failure.toast.text ?? null)
      return false
    })()

    inFlight[id] = settled
    settled.finally(() => {
      if (inFlight[id] === settled) delete inFlight[id]
    })
    return settled
  }

  // Undo the most recent landed review: un-review it on the server (points and
  // notification come back from an approve; the moment and its visibility from
  // a reject/delete) and jump back to it so it can be re-reviewed. A failed undo
  // keeps its stack entry so U can retry.
  const undoLast = async () => {
    if (undoBusy) return
    const id = undoStack[undoStack.length - 1]
    if (!id) return

    undoBusy = true
    try {
      await inFlight[id]
      if (!undoable(decisions[id])) {
        undoStack = undoStack.filter((stackedId) => stackedId !== id)
        notifyError("Nothing to undo — that review didn't go through")
        return
      }

      const result = await send('unreview', id, {})
      if (result.networkError) {
        notifyError('Network error — undo failed, press U to retry')
        return
      }
      if (!result.ok) {
        notifyError('Undo failed', result.message)
        return
      }

      // The queue still holds the item as it was loaded — pending, moment intact —
      // which is exactly what the undo restored.
      dropDecision(id)
      forgetRating(id)
      closePanels()

      // During the skipped pass the undone item may not be in the current
      // queue — the undo still happened, there's just nowhere to jump.
      const position = queueIds.indexOf(id)
      if (position >= 0) index = position
      onUndo?.(id)
      notifySuccess('Review undone')
    } finally {
      undoBusy = false
    }
  }

  // The level the server last confirmed, which a failed press falls back to.
  const confirmedLevel = (id) => confirmedLevelOf(ratings, id, { decisions, itemsById })

  // Rates an approved item: the pressed level shows at once and the request
  // queues behind this item's earlier presses and the approve that was in flight
  // when it was pressed. A press superseded by a later one neither writes the
  // shown level nor reports, but what it landed is still remembered as confirmed.
  const rate = (submission, level) => {
    const id = submission.id
    let ticket
    ;({ ratings, ticket } = pressRating(ratings, id))
    levelOverrides = { ...levelOverrides, [id]: level }
    const approval = inFlight[id]

    const run = async () => {
      const approved = approval ? await approval : true
      if (!latestRating(ratings, ticket)) return
      if (!approved) {
        // The approve failed (its own toast said so): there is nothing to rate.
        forgetRating(id)
        return
      }
      const result = await send('rate', id, { level })
      let outcome
      ;({ ratings, outcome } = settleRating(ratings, ticket, { ok: result.ok, level }))
      if (outcome !== 'failed') return
      levelOverrides = { ...levelOverrides, [id]: confirmedLevel(id) }
      notifyError('Rating failed', result.message)
    }
    rateQueue[id] = (rateQueue[id] ?? Promise.resolve()).then(run, run)
    return rateQueue[id]
  }

  const onApprove = () => {
    if (!current || !isPending) return
    runDecision(current, 'approve', {}, { kind: 'approved' })
  }

  const onApproveExample = () => {
    if (!current || !isPending || questHasExample) return
    runDecision(current, 'approve', { example: true }, { kind: 'approved', example: true })
  }

  // One request approves and rates, so one Undo reverses both.
  const onApproveRated = (level) => {
    if (!current || !isPending) return
    runDecision(current, 'approve', { curation_level: level }, { kind: 'approved', curation_level: level })
  }

  // 1 / 2 / 3: approve-and-rate a pending item; toggle the rating on an approved one.
  const onRateKey = (level) => {
    if (!current) return
    if (isPending) onApproveRated(level)
    else if (isApproved) rate(current, currentLevel === level ? 'none' : level)
  }

  const openRejectPanel = () => {
    rejectCategory = ''
    rejectReason = ''
    selectedRuleId = null
    deleteOpen = false
    rejectOpen = true
  }

  const openDeletePanel = () => {
    rejectOpen = false
    deleteOpen = true
  }

  const onRuleClick = (rule) => {
    selectedRuleId = rule.id
    rejectCategory = 'rules_not_followed'
    rejectReason = rule.text
  }

  const toggleCategory = (value) => {
    rejectCategory = rejectCategory === value ? '' : value
    selectedRuleId = null
  }

  const confirmRejectPanel = () => {
    if (!current || !isPending) return
    runDecision(
      current,
      'reject',
      { rejection_category: rejectCategory, rejection_reason: rejectReason },
      { kind: 'rejected' }
    )
  }

  const confirmDeletePanel = () => {
    if (!current || !isPending) return
    runDecision(current, 'delete', {}, { kind: 'deleted' })
  }

  const onSkip = () => {
    if (!current) {
      advance()
      return
    }
    if (isPending) decisions = skipDecision(decisions, current.id)
    advance()
  }

  // Keyboard shortcuts. Skipped while typing in a field; Enter confirms the
  // open reject/delete panel via its own scoped listener instead.
  const onKeydown = (e) => {
    if (e.repeat) return
    const target = e.target
    const tag = target.tagName
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable || e.metaKey || e.ctrlKey || e.altKey) {
      if (e.key === 'Escape') target.blur?.()
      return
    }

    // An open panel owns the keyboard: focus lands on its chips and buttons, so
    // a typed "a" or "e" must not approve the submission underneath.
    if ((rejectOpen || deleteOpen) && e.key !== 'Escape') return

    switch (e.key) {
      case 'Escape':
        if (rejectOpen || deleteOpen) closePanels()
        else onExit()
        break
      case 'ArrowRight': onSkip(); break
      case 'ArrowLeft': goBack(); break
      case 'a': case 'A': onApprove(); break
      case 'r': case 'R': if (isPending) openRejectPanel(); break
      case 'd': case 'D': if (isPending) openDeletePanel(); break
      case 'e': case 'E': onApproveExample(); break
      case 's': case 'S': onSkip(); break
      case 'u': case 'U': undoLast(); break
      default:
        if (!levelByKey[e.key]) return
        onRateKey(levelByKey[e.key])
    }
    e.preventDefault()
  }

  $effect(() => {
    window.addEventListener('keydown', onKeydown)
    return () => window.removeEventListener('keydown', onKeydown)
  })

  // Enter confirms the open panel from anywhere except a text field, where it
  // types as usual. These are inline panels rather than modals because a modal
  // fights the global keyboard shortcuts.
  $effect(() => {
    if (!rejectOpen && !deleteOpen) return
    const confirmPanel = rejectOpen ? confirmRejectPanel : confirmDeletePanel
    const onEnter = (e) => {
      if (e.key !== 'Enter') return
      const tag = e.target.tagName
      if (tag === 'TEXTAREA' || tag === 'INPUT') return
      e.preventDefault()
      confirmPanel()
    }
    window.addEventListener('keydown', onEnter)
    return () => window.removeEventListener('keydown', onEnter)
  })

  const counts = $derived(decisionCounts(decisions))

  const skippedIds = $derived(queueIds.filter((id) => decisions[id]?.kind === 'skipped'))

  const onReviewSkipped = () => {
    const ids = skippedIds
    const next = { ...decisions }
    ids.forEach((id) => delete next[id])
    decisions = next
    queueIds = ids
    phase = 'skipped'
    index = 0
    closePanels()
  }

  const atEnd = $derived(index >= queueIds.length)
  const queueExhausted = $derived(atEnd && !(phase === 'main' && queue.hasMore))

  const rules = $derived.by(() => {
    const nodes = current?.quest?.quest_template?.quest_rules || []
    return [...nodes].sort((a, b) => a.position - b.position)
  })

  const exampleThumbUrl = $derived(current?.quest?.example_thumbnail_url)

  const positionLabel = $derived(
    phase === 'main'
      ? `${Math.min(index + 1, queueIds.length)} of ${queue.totalCount ?? '…'}`
      : `${Math.min(index + 1, queueIds.length)} of ${queueIds.length} skipped`
  )
</script>

{#snippet undoButton()}
  <span title="Un-review the last decision and jump back to it">
    <!-- The light disabled button vanishes on this dark surface. -->
    <Button
      variant="default"
      size="sm"
      icon="undo"
      class="disabled:bg-neutral-content/10 disabled:text-neutral-content/50"
      disabled={undoStack.length === 0 || undoBusy}
      onclick={undoLast}
    >
      Undo
    </Button>
  </span>
{/snippet}

<div class="fixed inset-0 lg:left-72 z-40 bg-neutral text-neutral-content flex overflow-hidden">
  <ActionToast {toast} ondismiss={() => (toast = null)} />

  {#if queueExhausted}
    <div class="m-auto text-center">
      <h2 class="text-2xl font-bold mb-2">{queueIds.length === 0 ? 'Nothing to review' : 'Queue complete 🎉'}</h2>
      <p class="text-neutral-content/60 mb-6">
        {counts.approved} approved · {counts.rejected} rejected · {counts.deleted} deleted · {counts.skipped} skipped
      </p>
      <div class="flex gap-3 justify-center">
        <Button variant="secondary" onclick={onExit}>{exitLabel}</Button>
        {#if skippedIds.length > 0}
          <Button variant="default" onclick={onReviewSkipped}>Review {skippedIds.length} skipped</Button>
        {/if}
        {#if undoStack.length > 0}{@render undoButton()}{/if}
      </div>
    </div>
  {:else if !current}
    <div class="m-auto text-neutral-content/60">Loading…</div>
  {:else}
    <div class="flex-1 flex flex-col min-w-0 p-5">
      <div class="flex-1 flex items-center justify-center gap-3 min-h-0">
        {#if current.moment?.video_url}
          {#key current.moment.video_url}
            <video
              src={current.moment.video_url}
              poster={current.moment.thumbnail_url || undefined}
              controls
              autoplay
              muted
              loop
              playsinline
              class="max-w-full max-h-full rounded-box"
            ></video>
          {/key}
        {:else if current.moment?.main_image_url || current.moment?.selfie_image_url}
          {#if current.moment.main_image_url}
            <div class="flex-1 h-full flex items-center justify-center min-w-0">
              <img src={current.moment.main_image_url} alt="Main capture" class="max-w-full max-h-full object-contain rounded-box" />
            </div>
          {/if}
          {#if current.moment.selfie_image_url}
            <div class="flex-1 h-full flex items-center justify-center min-w-0">
              <img src={current.moment.selfie_image_url} alt="Selfie capture" class="max-w-full max-h-full object-contain rounded-box" />
            </div>
          {/if}
        {:else}
          <span class="text-neutral-content/50">No media attached.</span>
        {/if}
      </div>

      <div class="flex items-center gap-4 pt-3 flex-wrap text-sm">
        {#if current.user}
          <a href={`/users/${current.user.id}/edit`} target="_blank" rel="noopener" class="font-semibold link link-hover">
            {current.user.name}
          </a>
        {:else}
          <span class="font-semibold">Deleted user</span>
        {/if}
        <span>{current.place?.display_name || current.place?.name || 'No place'}</span>
        <VisibilityBadge visibility={current.moment?.visibility} />
        <span class="text-neutral-content/50">
          Submitted {current.moment?.created_at ? dayjs(current.moment.created_at).format('ll LT') : '—'}
        </span>
      </div>
    </div>

    <div class="w-[35%] min-w-[340px] max-w-[460px] border-l border-neutral-content/10 p-5 overflow-y-auto flex flex-col gap-4">
      <div class="flex items-center justify-between">
        <button type="button" class="text-neutral-content/60 hover:text-neutral-content" onclick={onExit}>✕ Exit</button>
        <span class="text-neutral-content/60 text-sm flex items-center gap-1">
          {positionLabel} · ✓ {counts.approved} · ✗ {counts.rejected} ·
          <Icon name="delete" size="xs" /> {counts.deleted} · ↷ {counts.skipped}
        </span>
      </div>

      <div>
        <div class="text-xs text-neutral-content/60 mb-1">
          {current.quest.quest_template?.display_name || current.quest.quest_template?.name || 'Archived quest'}
          {#if current.quest.quest_template?.capture_mode}
            · {CAPTURE_MODE_LABELS[current.quest.quest_template.capture_mode] || current.quest.quest_template.capture_mode}
          {/if}
        </div>
        {#if current.quest.quest_template?.reward_adventure}
          {@const reward = current.quest.quest_template.reward_adventure}
          <div class="text-xs text-warning mb-1 flex items-center gap-1">
            <Icon name="reward" size="xs" />
            Approving grants: {reward.title}{#if reward.place_name}&nbsp;({reward.place_name}){/if}
          </div>
        {/if}
        <h3 class="text-lg font-bold m-0">{current.quest.prompt}</h3>
        {#if current.quest.prompt_description}
          <p class="text-neutral-content/70 text-sm mt-1">{current.quest.prompt_description}</p>
        {/if}
      </div>

      {#if rules.length > 0}
        <div>
          <div class="text-xs text-neutral-content/60 mb-1">Rules</div>
          <div class="flex flex-col gap-1">
            {#each rules as rule (rule.id)}
              <div class="text-sm">{rule.emoji ? `${rule.emoji} ` : '• '}{rule.text}</div>
            {/each}
          </div>
        </div>
      {/if}

      {#if exampleThumbUrl}
        <div>
          <div class="text-xs text-neutral-content/60 mb-1">Example (reference)</div>
          <ClickableImage url={exampleThumbUrl} title="Quest Example" thumbClass="h-24 w-24 object-contain bg-base-200 rounded-box" />
        </div>
      {/if}

      {#if currentDecision}
        <div class="text-warning text-sm">
          {#if currentDecision.kind === 'approved'}Approved just now. Press U to undo.{/if}
          {#if currentDecision.kind === 'rejected'}Rejected just now. Press U to undo.{/if}
          {#if currentDecision.kind === 'deleted'}Deleted just now. Press U to undo.{/if}
          {#if currentDecision.kind === 'skipped'}Skipped — still in the queue.{/if}
          {#if currentDecision.kind === 'already_reviewed'}Reviewed by someone else.{/if}
        </div>
      {:else if current.state !== 'submitted'}
        <div class="text-warning text-sm">
          {current.state === 'approved' ? 'Approved' : 'Rejected'}
          {current.reviewed_by?.name ? ` by ${current.reviewed_by.name}` : ''}
          {current.reviewed_at ? ` on ${dayjs(current.reviewed_at).format('ll')}` : ''}.
        </div>
      {/if}

      <div class="mt-auto flex flex-col gap-3">
        {#if isPending && !rejectOpen && !deleteOpen}
          <div class="flex flex-wrap gap-2">
            <Button variant="success" size="sm" icon="check" onclick={onApprove}>Approve</Button>
            <Button
              variant="error"
              size="sm"
              icon="close"
              title="Reject — locks the moment private; the user can re-submit"
              onclick={openRejectPanel}
            >
              Reject
            </Button>
            <Button
              variant="error"
              outline
              size="sm"
              icon="delete"
              title="Delete the moment (TOS violation); the user can re-submit"
              onclick={openDeletePanel}
            >
              Delete
            </Button>
            {#each curationLevels as level (level.value)}
              <Button
                variant="warning"
                size="sm"
                icon="star"
                title={`Approve & rate ${level.label} — ${level.definition}`}
                onclick={() => onApproveRated(level.value)}
              >
                {level.label}
              </Button>
            {/each}
            <Button
              variant="secondary"
              size="sm"
              disabled={questHasExample}
              title={questHasExample ? 'This quest already has an Example' : "Approve & mark as the quest's Example"}
              onclick={onApproveExample}
            >
              ⭑ Example
            </Button>
          </div>
        {/if}

        {#if rejectOpen && isPending}
          <div class="bg-neutral-content/5 rounded-box p-3">
            <div class="font-semibold mb-2">Why is this rejected?</div>

            {#if rules.length > 0}
              <div class="flex flex-col gap-1 mb-3">
                {#each rules as rule (rule.id)}
                  <button
                    type="button"
                    class="text-left px-2 py-1 rounded-box border text-sm {selectedRuleId === rule.id ? 'border-error' : 'border-neutral-content/20'}"
                    onclick={() => onRuleClick(rule)}
                  >
                    {rule.emoji ? `${rule.emoji} ` : ''}{rule.text}
                  </button>
                {/each}
              </div>
            {/if}

            <div class="flex flex-wrap gap-1 mb-3">
              {#each rejectionCategories as option (option.value)}
                <button
                  type="button"
                  class="badge cursor-pointer {rejectCategory === option.value ? 'badge-error' : 'badge-outline'}"
                  onclick={() => toggleCategory(option.value)}
                >
                  {option.label}
                </button>
              {/each}
            </div>

            <textarea
              class="textarea w-full text-sm"
              rows="3"
              placeholder="Feedback shown to the user (optional)…"
              bind:value={rejectReason}
            ></textarea>

            <div class="flex justify-end gap-2 mt-2">
              <Button variant="ghost" size="sm" onclick={() => (rejectOpen = false)}>Cancel</Button>
              <Button variant="error" size="sm" onclick={confirmRejectPanel}>Confirm reject</Button>
            </div>
          </div>
        {/if}

        {#if deleteOpen && isPending}
          <div class="bg-neutral-content/5 rounded-box p-3 border border-error/40">
            <div class="font-semibold mb-2">Delete this moment?</div>
            <p class="text-sm text-neutral-content/70">
              For TOS violations. The user's moment is permanently deleted, the quest returns to Ready so they can
              re-submit, and they see "Inappropriate content" as the rejection reason.
            </p>
            <div class="flex justify-end gap-2 mt-3">
              <Button variant="ghost" size="sm" onclick={() => (deleteOpen = false)}>Cancel</Button>
              <Button variant="error" size="sm" onclick={confirmDeletePanel}>Confirm delete ⏎</Button>
            </div>
          </div>
        {/if}

        {#if !isPending && isApproved}
          <div class="flex flex-wrap gap-2">
            {#each curationLevels as level (level.value)}
              {@const active = currentLevel === level.value}
              <Button
                variant={active ? 'warning' : 'ghost'}
                size="sm"
                icon="star"
                class={active ? '' : 'text-neutral-content/70'}
                title={active ? 'Click again to clear the rating' : `Rate ${level.label} — ${level.definition}`}
                aria-pressed={active}
                onclick={() => rate(current, active ? 'none' : level.value)}
              >
                {level.label}
              </Button>
            {/each}
          </div>
        {/if}

        {#if (isPending && !rejectOpen && !deleteOpen) || (!isPending && isApproved)}
          <p class="text-xs text-neutral-content/50 m-0">{definitionsLine}</p>
        {/if}

        <div class="flex gap-2">
          <Button variant="default" size="sm" icon="back" disabled={index === 0} onclick={goBack}>Previous</Button>
          <Button variant="default" size="sm" iconEnd="chevron_right" onclick={onSkip}>Skip</Button>
          {@render undoButton()}
        </div>

        <div class="flex flex-wrap gap-x-3 gap-y-1 pt-2 border-t border-neutral-content/10 text-xs text-neutral-content/60">
          <span><kbd class="kbd kbd-xs bg-neutral-content/10 text-neutral-content border-neutral-content/30">A</kbd> Approve</span>
          <span><kbd class="kbd kbd-xs bg-neutral-content/10 text-neutral-content border-neutral-content/30">R</kbd> Reject</span>
          <span><kbd class="kbd kbd-xs bg-neutral-content/10 text-neutral-content border-neutral-content/30">D</kbd> Delete</span>
          {#if curationLevels.length > 0}
            <span>
              {#each curationLevels as level, position (level.value)}{#if position > 0}/{/if}<kbd class="kbd kbd-xs bg-neutral-content/10 text-neutral-content border-neutral-content/30">{level.shortcut}</kbd>{/each}
              {curationLevels.map((level) => level.label).join(' / ')}
            </span>
          {/if}
          <span><kbd class="kbd kbd-xs bg-neutral-content/10 text-neutral-content border-neutral-content/30">E</kbd> Example</span>
          <span><kbd class="kbd kbd-xs bg-neutral-content/10 text-neutral-content border-neutral-content/30">S</kbd>/<kbd class="kbd kbd-xs bg-neutral-content/10 text-neutral-content border-neutral-content/30">→</kbd> Skip</span>
          <span><kbd class="kbd kbd-xs bg-neutral-content/10 text-neutral-content border-neutral-content/30">←</kbd> Previous</span>
          <span><kbd class="kbd kbd-xs bg-neutral-content/10 text-neutral-content border-neutral-content/30">U</kbd> Undo</span>
          <span><kbd class="kbd kbd-xs bg-neutral-content/10 text-neutral-content border-neutral-content/30">Esc</kbd> Exit</span>
        </div>
      </div>
    </div>
  {/if}
</div>
